import { BoundingSphere, Matrix3, Matrix4, Quaternion, Cartesian3, Cartesian2, } from "cesium";
/**
 * Calculate the angle between two points on the screen
 * @param center2D center point
 * @param startPosition start position
 * @param endPosition current position
 * @returns [angle (radians), true if the rotation is clockwise, false otherwise]
 */
export function calculateRotationAngleAndDirection(center2D, startPosition, endPosition) {
    const vec1 = Cartesian2.subtract(startPosition, center2D, new Cartesian2());
    if (Cartesian2.magnitude(vec1) === 0)
        return [0, false];
    Cartesian2.normalize(vec1, vec1);
    const vec2 = Cartesian2.subtract(endPosition, center2D, new Cartesian2());
    if (Cartesian2.magnitude(vec2) === 0)
        return [0, false];
    Cartesian2.normalize(vec2, vec2);
    const angle = Cartesian2.angleBetween(vec1, vec2);
    const cross = Cartesian3.cross(new Cartesian3(vec1.x, vec1.y, 0), new Cartesian3(vec2.x, vec2.y, 0), new Cartesian3());
    if (Number.isNaN(cross.x) ||
        Number.isNaN(cross.y) ||
        Number.isNaN(cross.z) ||
        Cartesian3.magnitude(cross) === 0) {
        return [0, false];
    }
    Cartesian3.normalize(cross, cross);
    const isClockwise = Cartesian3.dot(Cartesian3.UNIT_Z, cross) > 0;
    return [angle, isClockwise];
}
/**
 * Calculate the scaling factor
 * @param center2D center point
 * @param referencePoint2D reference point (usually the initial corner point)
 * @param currentPoint2D point corresponding to the current mouse position
 * @param minScale minimum scale value (default: 0.05)
 * @returns scaling factor
 */
export function calculateScaleFactor(center2D, referencePoint2D, currentPoint2D, minScale = 0.05) {
    const centerToEdge = Cartesian2.subtract(referencePoint2D, center2D, new Cartesian2());
    if (Cartesian2.magnitude(centerToEdge) === 0)
        return 1.0;
    const centerToEdgeVec = Cartesian2.normalize(centerToEdge, new Cartesian2());
    const imageSize = Cartesian2.magnitude(centerToEdge);
    const centerToMouseEnd = Cartesian2.subtract(currentPoint2D, center2D, new Cartesian2());
    const centerToMouseEndVec = Cartesian2.normalize(centerToMouseEnd, new Cartesian2());
    const centerToMouseEndMag = Cartesian2.magnitude(centerToMouseEnd);
    // if the direction is opposite, maintain the current scale
    if (Cartesian2.dot(centerToMouseEndVec, centerToEdgeVec) < 0) {
        return 1.0;
    }
    const scaleFactor = centerToMouseEndMag / imageSize;
    return Math.max(scaleFactor, minScale);
}
/**
 * Rotate points around the center of the bounding sphere
 * @param points points to rotate
 * @param angle rotation angle (radians)
 * @param isClockwise true if the rotation is clockwise, false otherwise
 * @returns rotated points
 */
export function rotatePoints(points, angle, isClockwise) {
    const originalAxis = new Cartesian3(0, 0, 1);
    if (isClockwise) {
        Cartesian3.negate(originalAxis, originalAxis);
    }
    const quaternion = Quaternion.fromAxisAngle(originalAxis, angle);
    const rotationMatrix = Matrix3.fromQuaternion(quaternion);
    const center = BoundingSphere.fromPoints(points).center;
    // move points to the center
    const pointsRelativeToCenter = points.map((point) => Cartesian3.subtract(point, center, new Cartesian3()));
    // apply rotation
    const rotatedPoints = pointsRelativeToCenter.map((point) => Matrix3.multiplyByVector(rotationMatrix, point, new Cartesian3()));
    // move points back to the original position
    return rotatedPoints.map((point) => Cartesian3.add(point, center, new Cartesian3()));
}
/**
 * Translate points
 * @param points points to translate
 * @param delta translation vector
 * @returns translated points
 */
export function translatePoints(points, delta) {
    return points.map((point) => Cartesian3.add(point, delta, new Cartesian3()));
}
/**
 * Scale points
 * @param points points to scale
 * @param value scaling factor
 * @returns scaled points
 */
export function scalePoints(points, value) {
    const center = BoundingSphere.fromPoints(points).center;
    return points.map((point) => {
        const vec = Cartesian3.subtract(point, center, new Cartesian3());
        return Cartesian3.add(center, Cartesian3.multiplyByScalar(vec, value, new Cartesian3()), new Cartesian3());
    });
}
/**
 * Create a model matrix for rotation
 * @param oldCenter rotation center point
 * @param angle rotation angle (radians)
 * @param isClockwise true if the rotation is clockwise, false otherwise
 * @returns rotation model matrix
 */
export function createRotationModelMatrix(oldCenter, angle, isClockwise) {
    // rotation axis (ECEF coordinate system's up vector)
    const rotationAxis = Cartesian3.normalize(oldCenter, new Cartesian3());
    if (isClockwise) {
        Cartesian3.negate(rotationAxis, rotationAxis);
    }
    // create rotation quaternion
    const quaternion = Quaternion.fromAxisAngle(rotationAxis, angle);
    // move center to origin
    const translationToOrigin = Matrix4.fromTranslation(Cartesian3.negate(oldCenter, new Cartesian3()));
    // create rotation matrix
    const rotationMatrix3 = Matrix3.fromQuaternion(quaternion);
    const rotationMatrix4 = Matrix4.fromRotationTranslation(rotationMatrix3, Cartesian3.ZERO);
    // move origin to center
    const translationFromOrigin = Matrix4.fromTranslation(oldCenter);
    // calculate final transformation matrix
    return Matrix4.multiply(translationFromOrigin, Matrix4.multiply(rotationMatrix4, translationToOrigin, new Matrix4()), new Matrix4());
}
/**
 * Create a model matrix for translation
 * @param oldCenter original center point
 * @param newCenter new center point
 * @returns translation model matrix
 */
export function createTranslationModelMatrix(oldCenter, newCenter) {
    const translationVector = Cartesian3.subtract(newCenter, oldCenter, new Cartesian3());
    return Matrix4.fromTranslation(translationVector);
}
/**
 * Create a model matrix for scaling
 * @param center scaling center point
 * @param scale scaling factor
 * @returns scaling model matrix
 */
export function createScaleModelMatrix(center, scale) {
    // move center to origin
    const translationToOrigin = Matrix4.fromTranslation(Cartesian3.negate(center, new Cartesian3()));
    // scaling matrix
    const scaleMatrix = Matrix4.fromScale(new Cartesian3(scale, scale, scale));
    // move origin to center
    const translationFromOrigin = Matrix4.fromTranslation(center);
    // calculate final transformation matrix
    return Matrix4.multiply(translationFromOrigin, Matrix4.multiply(scaleMatrix, translationToOrigin, new Matrix4()), new Matrix4());
}
//# sourceMappingURL=spatial-transform-util.js.map